import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * Renders an uploaded HTML presentation inside an isolated <iframe>.
 * Uses RELATIVE URL (/api/...) so it works on any domain.
 */
const PresentationFrame = ({
  presentationId,
  heightMode = 'auto',
  fixedHeight = 800,
  fullWidth = true,
  showOpenButton = false,
}) => {
  const iframeRef = useRef(null);
  const [autoHeight, setAutoHeight] = useState(600);
  const [loaded, setLoaded] = useState(false);

  const src = `/api/cms/presentations/${presentationId}`;

  useEffect(() => {
    if (heightMode !== 'auto') return;
    let interval = null;
    let observer = null;
    const measure = () => {
      const ifr = iframeRef.current;
      if (!ifr) return;
      try {
        const doc = ifr.contentDocument;
        if (!doc || !doc.body) return;
        const h = Math.max(
          doc.body.scrollHeight, doc.documentElement.scrollHeight,
          doc.body.offsetHeight, doc.documentElement.offsetHeight
        );
        if (h && Math.abs(h - autoHeight) > 4) setAutoHeight(h);
      } catch (e) { /* cross-origin fallback */ }
    };
    const onLoad = () => {
      setLoaded(true);
      measure();
      try {
        const ifr = iframeRef.current;
        if (ifr && ifr.contentDocument?.body && 'ResizeObserver' in window) {
          observer = new ResizeObserver(measure);
          observer.observe(ifr.contentDocument.body);
        }
      } catch (e) { /* ignore */ }
      let ticks = 0;
      interval = setInterval(() => {
        measure();
        ticks++;
        if (ticks > 30) { clearInterval(interval); interval = null; }
      }, 300);
    };
    const ifr = iframeRef.current;
    if (ifr) ifr.addEventListener('load', onLoad);
    return () => {
      if (ifr) ifr.removeEventListener('load', onLoad);
      if (interval) clearInterval(interval);
      if (observer) observer.disconnect();
    };
  }, [heightMode, presentationId, autoHeight]);

  if (!presentationId) {
    return (
      <div className="py-12 text-center text-slate-500 border-2 border-dashed border-slate-300 rounded-lg mx-4">
        Vyberte prezentaci v editoru tohoto bloku.
      </div>
    );
  }

  const iframeHeight =
    heightMode === 'viewport' ? '100vh' :
    heightMode === 'fixed' ? `${fixedHeight}px` :
    `${autoHeight}px`;

  return (
    <div className={fullWidth ? 'w-full' : 'max-w-7xl mx-auto px-4'}>
      {showOpenButton && (
        <div className="flex justify-end mb-3 px-4">
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
            Otevřít v novém okně
          </a>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={src}
        title="Prezentace"
        style={{
          width: '100%',
          height: iframeHeight,
          border: 'none',
          display: 'block',
          background: 'transparent',
          transition: heightMode === 'auto' ? 'height 0.25s ease' : 'none',
        }}
        loading="lazy"
        scrolling={heightMode === 'auto' ? 'no' : 'auto'}
        data-testid="presentation-iframe"
      />
      {!loaded && (
        <div className="text-center text-xs text-slate-400 py-2">Načítám prezentaci...</div>
      )}
    </div>
  );
};

export default PresentationFrame;
